"use client";

/**
 * useJobPoller — exponential backoff poller for in-flight analysis jobs.
 *
 * Intervals: 2s → 3s → 5s → 8s → 10s → 15s (mirrors React AnalysisPoller exactly)
 */
import { useRef, useCallback } from "react";
import { getJobStatus } from "../api";
import type { JobStatusResponse, BackendAnalysisResponse } from "../types";

interface PollerCallbacks {
  onUpdate: (status: JobStatusResponse) => void;
  onComplete: (result: BackendAnalysisResponse) => void;
  onError: (error: string) => void;
}

const INTERVALS = [2000, 3000, 5000, 8000, 10000, 15000];

export function useJobPoller() {
  const isPollingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const intervalIndexRef = useRef(0);

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    intervalIndexRef.current = 0;
  }, []);

  const startPolling = useCallback(
    async (
      jobId: string,
      callbacks: PollerCallbacks
    ) => {
      if (isPollingRef.current) stopPolling();

      isPollingRef.current = true;
      intervalIndexRef.current = 0;

      while (isPollingRef.current) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
          const status = await getJobStatus(
            jobId,
            abortControllerRef.current.signal
          );

          if (!isPollingRef.current) break;

          callbacks.onUpdate(status);

          if (status.status === "SUCCESS") {
            callbacks.onComplete(status.result!);
            stopPolling();
            break;
          } else if (status.status === "FAILURE") {
            callbacks.onError(status.error || "Analysis failed");
            stopPolling();
            break;
          }

          // Wait with exponential backoff
          const delay =
            INTERVALS[Math.min(intervalIndexRef.current, INTERVALS.length - 1)];
          if (intervalIndexRef.current < INTERVALS.length - 1) {
            intervalIndexRef.current++;
          }

          await new Promise<void>((resolve) => setTimeout(resolve, delay));
        } catch (err: any) {
          if (err?.name === "AbortError") break;
          console.error("Polling error:", err);
          await new Promise<void>((resolve) => setTimeout(resolve, 5000));
        }
      }
    },
    [stopPolling]
  );

  return { startPolling, stopPolling };
}
